export type IntegrationStatus =
	| "New - Pre-Integration"
	| "Integrating"
	| "Testing"
	| "Ready to Go Live"
	| "Live"
	| "On-Hold"
	| "Abandoned";

export type OnboardingStatus =
	| "Not Started"
	| "Onboarding in Progress"
	| "Onboarded";

export type WishlistStatus = "Sandbox" | "Preview" | "Production";

export type Chain = "Stellar" | "Solana" | "Polygon";

export type Asset = "USDC" | "USDT" | "EURC" | "ETH" | "SOL";

export type Product = "Deposit" | "Withdraw";

export type ProductType = "deposit" | "withdraw" | "both";

export type CustodialType = "Custodial" | "Non-Custodial";

export type TestResultStatus = "submitted" | "certified" | "needs-revision";

export type MAURange =
	| "0-10K"
	| "10K-50K"
	| "50K-100K"
	| "100K-500K"
	| "500K-1M"
	| "1M+";

export interface MainContact {
	name: string;
	email: string;
	phoneNumber: string;
	role: string;
}

export interface FeeStructure {
	start_range?: number;
	end_range?: number;
	startRange?: number;
	endRange?: number;
	fee: number;
	fee_type?: "percentage" | "flat";
	feeType?: "percentage" | "flat";
}

export interface DocumentSubtask {
	sent: boolean;
	sentAt?: string;
	sentBy?: string;
	received: boolean;
	receivedAt?: string;
	receivedBy?: string;
	documentUrl?: string;
	documentName?: string;
}

export interface ChecklistItem {
	id: string;
	label: string;
	completed: boolean;
	completedBy?: string;
	completedAt?: string;
	link?: string;
	kybFormId?: string; // Link to KYB application
	kybProgress?: number; // Progress percentage 0-100
	subtasks?: DocumentSubtask;
	documentUrl?: string; // URL of uploaded document
	documentName?: string; // Name of uploaded document
	uploadedAt?: string; // Timestamp of upload
	uploadedBy?: string; // User who uploaded
}

export interface DocumentTemplate {
	id: string;
	name: string;
	description: string;
	url: string;
	checklistItemId: string; // Maps to ChecklistItem.id (e.g., 'nda-signed', 'consent-form', etc.)
	createdAt: string;
	updatedAt: string;
}

export interface Comment {
	id: string;
	text: string;
	author: string;
	timestamp: string;
}

export interface User {
	id: string;
	email: string;
	name: string;
	role: "admin" | "partner" | "stellar_admin";
	wallet_ids: string[]; // Wallets this user has access to (snake_case for tRPC/DB parity)
	created_at: string;
	last_login?: string;
}

export interface PartnerTask {
	task_name: string;
	deadline?: string;
	dri?: string;
	progress: "pending" | "in_progress" | "completed";
	comments?: string;
}

export interface CustomerSuccess {
	kam?: string;
	tier?: 1 | 2 | 3;
	meeting_frequency?: "Every two weeks" | "Monthly" | "Quarterly" | "As needed";
	tasks_progress?: number;
	missing_items_count?: number;
}

export interface MarketingGrant {
	total_grant_usd?: number;
	funds_left?: number;
	grant_expiration_date?: string;
	grant_status?: "Active" | "Expired" | "Pending" | "Completed";
	denomination?: string;
	quarter_year?: string;
	geos?: string[];
	agreement_link?: string;
}

export interface ImportResultDetail {
	partnerName: string;
	walletId: string;
	walletName: string;
	fields: string[];
	action: "update" | "create";
}

export interface ImportResult {
	totalPartners: number;
	matched: number;
	updated: number;
	created: number;
	unmatched: string[];
	details: ImportResultDetail[];
}

export interface Transaction {
	id: string;
	type: "Deposit" | "Withdraw";
	referenceNumber: string;
	timestamp: string;
}

/** Snake_case checklist item from BFF (matches DB shape) */
export interface ChecklistItemSnake {
	completed?: boolean;
	completed_at?: string;
	completed_by?: string;
	document_url?: string;
	document_name?: string;
	uploaded_at?: string;
	link?: string;
	kyb_form_id?: string;
	kyb_progress?: number;
	kyb_status?: string;
	subtasks?: DocumentSubtask;
}

/** Snake_case wallet — matches BFF/DB shape, no casing transforms */
export interface Wallet {
	id: string;
	wallet_name: string;
	company_name: string;
	custodial_type: CustodialType;
	chain: Chain;
	asset: Asset;
	products: Product[];
	main_contact_name?: string;
	main_contact_email?: string;
	main_contact_phone?: string;
	main_contact_role?: string;

	// Technical Information
	slack_channel?: string;
	home_domain?: string;
	funding_address?: string;
	deposit_address?: string;
	authentication_address?: string;
	agent_id?: string;
	main_office_id?: string;

	// Optional fields
	conducts_kyc?: boolean;
	mau?: MAURange | number;
	country_of_incorporation?: string;
	priority_markets?: string[];

	// Status tracking
	integration_status: IntegrationStatus;
	onboarding_status: OnboardingStatus;
	is_existing_partner?: boolean;

	// Whitelisting
	is_whitelisted: boolean;
	whitelist_environments?: WishlistStatus[];

	// Integration checklist (snake_case)
	salesforce_opportunity_created?: ChecklistItemSnake | null;
	nda_signed?: ChecklistItemSnake | null;
	consent_form_signed?: ChecklistItemSnake | null;
	kyc_form_completed?: (ChecklistItemSnake & { kyb_status?: string }) | null;
	contract_signed?: ChecklistItemSnake | null;
	salesforce_case_created?: ChecklistItemSnake | null;
	salesforce_case_number?: string;
	partner_setup_notified?: boolean;
	slack_channel_created?: ChecklistItemSnake | null;
	integration_completed?: ChecklistItemSnake | null;

	// Fees
	has_own_fees?: boolean;
	fee_structures?: FeeStructure[];
	fees_pricing_contacted?: ChecklistItemSnake | null;
	partner_activated?: ChecklistItemSnake | null;

	// Testing
	testing_completed?: ChecklistItemSnake | null;
	product_type?: ProductType;
	test_results?: TestResult[];

	// Production
	whitelisted_in_production?: ChecklistItemSnake | null;
	go_live_verification?: ChecklistItemSnake | null;

	// Production Whitelisting Information
	production_home_domain?: string;
	production_funding_address?: string;
	production_deposit_address?: string;
	production_auth_address?: string;
	production_toml_file_domain?: string;
	excluded_countries?: string[];
	production_custodial_type?: "Custodial" | "Non-Custodial" | "Both";

	// Transactions
	transactions?: Transaction[];

	// Marketing
	marketing_follow_up?: ChecklistItemSnake | null;

	// Comments
	comments?: Comment[];

	// Metadata
	created_at: string;
	updated_at: string;

	// Partner proposals
	proposed_changes?: Record<string, unknown> | null;
	proposed_changes_by?: string | null;
	proposed_changes_at?: string | null;

	// Partner Tracker fields
	customer_success?: CustomerSuccess;
	marketing_grant?: MarketingGrant;
	partner_tasks?: PartnerTask[];
	use_case?: string;
	ramping_type?: "On-Ramping" | "Off-Ramping" | "Both";
}

export const COUNTRIES = [
	"United States",
	"Canada",
	"Mexico",
	"United Kingdom",
	"Germany",
	"France",
	"Spain",
	"Italy",
	"Netherlands",
	"Belgium",
	"Switzerland",
	"Austria",
	"Sweden",
	"Norway",
	"Denmark",
	"Finland",
	"Poland",
	"Portugal",
	"Greece",
	"Ireland",
	"Czech Republic",
	"Hungary",
	"Romania",
	"Bulgaria",
	"Croatia",
	"Slovakia",
	"Slovenia",
	"Australia",
	"New Zealand",
	"Japan",
	"South Korea",
	"Singapore",
	"Hong Kong",
	"Taiwan",
	"India",
	"Indonesia",
	"Thailand",
	"Malaysia",
	"Philippines",
	"Vietnam",
	"Brazil",
	"Argentina",
	"Chile",
	"Colombia",
	"Peru",
	"Venezuela",
	"Ecuador",
	"Uruguay",
	"Paraguay",
	"Bolivia",
	"South Africa",
	"Nigeria",
	"Kenya",
	"Egypt",
	"Morocco",
	"Ghana",
	"Ethiopia",
	"Tanzania",
	"Uganda",
	"Senegal",
	"Ivory Coast",
	"United Arab Emirates",
	"Saudi Arabia",
	"Qatar",
	"Kuwait",
	"Bahrain",
	"Oman",
	"Jordan",
	"Lebanon",
	"Israel",
	"Turkey",
	"Pakistan",
	"Bangladesh",
	"Sri Lanka",
	"Nepal",
	"Myanmar",
	"Cambodia",
	"Laos",
];

export const US_STATES = [
	"Alabama",
	"Alaska",
	"Arizona",
	"Arkansas",
	"California",
	"Colorado",
	"Connecticut",
	"Delaware",
	"Florida",
	"Georgia",
	"Hawaii",
	"Idaho",
	"Illinois",
	"Indiana",
	"Iowa",
	"Kansas",
	"Kentucky",
	"Louisiana",
	"Maine",
	"Maryland",
	"Massachusetts",
	"Michigan",
	"Minnesota",
	"Mississippi",
	"Missouri",
	"Montana",
	"Nebraska",
	"Nevada",
	"New Hampshire",
	"New Jersey",
	"New Mexico",
	"New York",
	"North Carolina",
	"North Dakota",
	"Ohio",
	"Oklahoma",
	"Oregon",
	"Pennsylvania",
	"Puerto Rico",
	"Rhode Island",
	"South Carolina",
	"South Dakota",
	"Tennessee",
	"Texas",
	"Utah",
	"Vermont",
	"Virginia",
	"Washington",
	"West Virginia",
	"Wisconsin",
	"Wyoming",
];

export const CANADIAN_PROVINCES = [
	"Alberta",
	"British Columbia",
	"Manitoba",
	"New Brunswick",
	"Newfoundland and Labrador",
	"Northwest Territories",
	"Nova Scotia",
	"Nunavut",
	"Ontario",
	"Prince Edward Island",
	"Quebec",
	"Saskatchewan",
	"Yukon",
];

// Board of Directors
export interface KYCDirector {
	isExistingOwner: boolean; // true if selected from existing owners
	ownerIndex?: number; // index in ownershipTiers array if isExistingOwner is true
	// Fields for new directors (not existing owners)
	fullName?: string;
	dateOfBirth?: string;
	nationality?: string;
	address?: string;
}

// UBO (Ultimate Beneficial Owner) for corporate owners
export interface KYCUltimateBeneficialOwner {
	ownerType: "Individual" | "Corporation";
	ownershipPercentage: number;
	// Individual fields
	fullName?: string;
	dateOfBirth?: string;
	nationality?: string;
	countryOfResidence?: string;
	stateOrProvince?: string;
	residentialAddress?: string;
	city?: string;
	postalCode?: string;
	ssn?: string; // US & Puerto Rico
	sin?: string; // Canada
	photoIdUrl?: string;
	secondPhotoIdUrl?: string; // Canada requires 2 forms of ID
	// Corporate fields
	companyName?: string;
	registrationNumber?: string;
	dateOfIncorporation?: string;
	registeredOfficeAddress?: string;
	taxId?: string;
	legalEntityType?: string;
}

// KYC Form Types
export interface KYCOwnershipTier {
	tierNumber: number;
	ownerType: "Individual" | "Corporation";
	ownershipPercentage: number;

	// Corporate owner fields (for ≥10% ownership)
	companyName?: string;
	registrationNumber?: string;
	dateOfIncorporation?: string;
	registeredOfficeAddress?: string;
	taxId?: string;
	legalEntityType?: string;
	// Nested UBOs (≥25% owners of the corporate owner)
	ultimateBeneficialOwners?: KYCUltimateBeneficialOwner[];

	// Individual UBO fields (for ≥10% ownership)
	fullName?: string;
	dateOfBirth?: string;
	nationality?: string;
	countryOfResidence?: string;
	stateOrProvince?: string;
	residentialAddress?: string;
	city?: string;
	postalCode?: string;
	ssn?: string; // US & Puerto Rico
	sin?: string; // Canada
	photoIdUrl?: string;
	secondPhotoIdUrl?: string; // Canada requires 2 forms of ID
	consentFormUrl?: string; // Georgia, Nevada, or Canada consent form
	secondConsentFormUrl?: string; // Canada requires 2 consent forms
	corporateOwnersList?: string;
}

export interface KYCLicense {
	id: string;
	licenseName: string;
	jurisdiction: string;
	expiryDate: string;
	licenseNumber: string;
}

export interface KYCContact {
	name: string;
	email: string;
	phone: string;
	role?: string;
}

export interface KYCFormData {
	// Step 1 - Company Basics
	businessLegalName: string;
	dba: string;
	previousBusinessNames: string;
	entityType: string;
	countryOfIncorporation: string;
	registrationNumber: string;
	dateOfIncorporation: string;
	registeredAddress: {
		address1: string;
		address2: string;
		city: string;
		stateProvince: string;
		zip: string;
		country: string;
	};
	website: string;
	businessDescription: string;
	expectedMonthlyVolume: number;
	proofOfBusinessUrl?: string;
	proofOfAddressUrl?: string;

	// Country-specific Step 1 fields (US, DE, CO overrides)
	federalTaxId?: string;
	vatId?: string;
	rutDocument?: string;

	// Step 2 - Wallet & Technical Details
	walletType: "Custodial" | "Non-Custodial" | "Hybrid";
	hostedStatus: "Hosted" | "Un-hosted" | "Hybrid";
	currentlyIntegratedWithStellar: boolean;
	allowP2PInternal: boolean;
	allowP2PExternal: boolean;
	virtualAssetsDetails: string;
	publicKeySharing: "Individual" | "Organization" | "Both";
	isWalletOwnerKYCRequired: boolean;
	walletHolderInfoCollected?: string;
	walletHolderInfoUpdateCircumstances?: string;
	walletHolderInfoUpdateFrequency?: string;
	isWalletHolderInfoUpdatedRegularly: boolean;
	omnibusAccountDescription?: string;
	otherFundingMethods?: string;

	// Step 3 - Ownership & UBO
	numberOfOwnershipTiers: number;
	ownershipTiers: KYCOwnershipTier[];
	consentForBackgroundChecks: boolean;
	anyOwnerConvicted: boolean;
	convictionDetails?: string;
	ownershipStructureChartUrl?: string;
	hasBoardOfDirectors?: boolean;
	boardOfDirectors: KYCDirector[];

	// Step 4 - Compliance & Risk
	isRegulatedEntity: boolean;
	licenses: KYCLicense[];
	complianceOfficer: KYCContact & { photoIdUrl?: string };
	sanctionsScreening: boolean;
	sanctionsVendor?: string;
	sanctionsScreenedItems?: string;
	blockchainMonitoring: boolean;
	monitoringVendor?: string;
	monitoringProcess?: string;
	monitoringThresholds?: string;
	regulatoryEnforcement: boolean;
	enforcementDetails?: string;
	riskAssessmentUrl?: string;
	complianceProgramUrl?: string;

	// Step 5 - Limits & Restrictions
	velocityLimits: string;
	transactionLimits: string;
	otherLimits: string;
	blockWalletHolders: boolean;
	blockingCircumstances?: string;
	blockingPreventionControls?: string;

	// Step 6 - Contacts & Final Uploads
	primaryBusinessContact: KYCContact;
	primaryProductContact: KYCContact;
	primaryLegalContact: KYCContact;
	primarySigner: KYCContact;

	// Metadata
	id: string;
	walletId?: string; // Link KYC form to wallet
	submittedBy?: string; // User ID who submitted
	currentStep: number;
	percentComplete: number; // Progress percentage 0-100
	lastSavedAt: string;
	status:
		| "draft"
		| "in-progress"
		| "submitted"
		| "under_review"
		| "approved"
		| "rejected";
	submittedAt?: string;
	approvedBy?: string; // Name of person who approved
	approvedAt?: string; // When it was approved
	reviewerComments?: Array<{
		id: string;
		author: string;
		text: string;
		timestamp: string;
		isInternal: boolean;
	}>;
	createdAt: string;
	updatedAt: string;
	hiddenFieldValues?: Record<string, unknown>;
}

// Test Case Management Types
export interface TestCase {
	id: string;
	name: string;
	productType: ProductType;
	isRequired?: boolean;
	instructions: string;
	requiredFields: {
		referenceNumber?: boolean;
		dateOfBirth?: boolean;
		transactionStatus?: boolean;
		amount?: boolean;
		fees?: boolean;
		amountReceived?: boolean;
		transactionId?: boolean;
	};
}

export interface TestResult {
	id: string;
	wallet_id?: string;
	test_case_id: string;
	test_case_name: string;

	// Status tracking
	status: TestResultStatus; // "submitted", "certified", "needs-revision"

	// Captured data
	reference_number?: string;
	date_of_birth?: string;
	transaction_status?: string;
	amount?: string;
	fees?: string;
	amount_received?: string;
	transaction_id?: string;

	// Screenshot
	screenshot_url?: string;

	// Metadata
	submitted_by: string;
	submitted_at: string;
	certified_by?: string;
	certified_at?: string;
	notes?: string;
	admin_notes?: string;
}
